/** Event schema, pool admission, window W, year maths, UTC day number. */

import { CARDS_PER_DAY } from './config.js';

export const GAME_NAME = 'Now and Then';
export const MIN_EVENTS = 56;
export const MIN_WINDOW = 7;
export const MAX_WINDOW = 60;
export const PRE_LAUNCH_DAYS = 61;

export type FailureCode = 'too_few_events' | 'duplicate_wikidata' | 'year_zero' | 'unfilled_day';

export class BuildFailure extends Error {
  constructor(
    readonly code: FailureCode,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'BuildFailure';
  }
}

export type HistoryEvent = {
  id: string;
  title: string;
  year: number;
  familiar: boolean;
  headliner: boolean;
  wikipedia: string;
  wikidata: string;
  secondSource: string;
  factCheckPass: boolean;
  factCheckSource: string;
  signedOffBy: string;
  signedOffAt: string;
};

export type ExcludedRow = { raw: unknown; reasons: string[] };
export type LoadedPool = { admitted: HistoryEvent[]; excluded: ExcludedRow[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function str(row: Record<string, unknown>, key: string): string | undefined {
  const value = row[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

function failYearZero(id?: string): never {
  throw new BuildFailure('year_zero', 'stored year of 0 is not allowed', id ? { id } : {});
}

/** Schema check: a row with no sign-off is rejected. Year 0 fails the build. */
export function parseEvent(
  raw: unknown,
): { ok: true; event: HistoryEvent } | { ok: false; reasons: string[] } {
  if (!isRecord(raw)) return { ok: false, reasons: ['row is not an object'] };

  const id = str(raw, 'id');
  const title = str(raw, 'title');
  const wikipedia = str(raw, 'wikipedia');
  const wikidata = str(raw, 'wikidata');
  const secondSource = str(raw, 'secondSource');
  const factCheckSource = str(raw, 'factCheckSource');
  const signedOffBy = str(raw, 'signedOffBy');
  const signedOffAt = str(raw, 'signedOffAt');
  const year = typeof raw.year === 'number' && Number.isInteger(raw.year) ? raw.year : undefined;
  if (year === 0) failYearZero(id);

  const familiar = typeof raw.familiar === 'boolean' ? raw.familiar : undefined;
  const headliner = typeof raw.headliner === 'boolean' ? raw.headliner : undefined;

  const reasons: string[] = [];
  if (!id) reasons.push('missing id');
  if (!title) reasons.push('missing title');
  if (!wikipedia) reasons.push('missing Wikipedia page');
  if (!wikidata) reasons.push('missing Wikidata item');
  if (!secondSource) reasons.push('missing second source');
  if (!factCheckSource) reasons.push('missing fact-check source');
  if (!signedOffBy) reasons.push('no person sign-off');
  if (!signedOffAt) reasons.push('missing signed-off time');
  if (year === undefined) reasons.push('missing integer year');
  if (familiar === undefined) reasons.push('missing familiar');
  if (headliner === undefined) reasons.push('missing headliner');
  if (raw.factCheckPass !== true) reasons.push('no fact-check pass');

  if (
    reasons.length > 0 ||
    id === undefined ||
    title === undefined ||
    wikipedia === undefined ||
    wikidata === undefined ||
    secondSource === undefined ||
    factCheckSource === undefined ||
    signedOffBy === undefined ||
    signedOffAt === undefined ||
    year === undefined ||
    familiar === undefined ||
    headliner === undefined
  ) {
    return { ok: false, reasons };
  }

  return {
    ok: true,
    event: {
      id,
      title,
      year,
      familiar,
      headliner,
      wikipedia,
      wikidata,
      secondSource,
      factCheckPass: true,
      factCheckSource,
      signedOffBy,
      signedOffAt,
    },
  };
}

function rowId(raw: unknown): string {
  return (isRecord(raw) && str(raw, 'id')) || '(no id)';
}

export function loadPool(rows: unknown[]): LoadedPool {
  const byQid = new Map<string, string[]>();
  for (const raw of rows) {
    const qid = isRecord(raw) ? str(raw, 'wikidata') : undefined;
    if (!qid) continue;
    const ids = byQid.get(qid) ?? [];
    ids.push(rowId(raw));
    byQid.set(qid, ids);
  }
  for (const [wikidata, ids] of byQid) {
    if (ids.length > 1) {
      throw new BuildFailure(
        'duplicate_wikidata',
        `Duplicate Wikidata ${wikidata}: ${ids.join(', ')}`,
        { wikidata, ids },
      );
    }
  }

  const admitted: HistoryEvent[] = [];
  const excluded: ExcludedRow[] = [];
  for (const raw of rows) {
    const parsed = parseEvent(raw);
    if (parsed.ok) admitted.push(parsed.event);
    else excluded.push({ raw, reasons: parsed.reasons });
  }
  return { admitted, excluded };
}

/** W = min(60, floor(n / CARDS_PER_DAY) - 1) for daily card count. */
export function computeWindow(n: number): number {
  return Math.min(MAX_WINDOW, Math.floor(n / CARDS_PER_DAY) - 1);
}

export function requireWindow(n: number): number {
  if (n < MIN_EVENTS) {
    throw new BuildFailure('too_few_events', `${n} signed-off events; minimum is ${MIN_EVENTS}`, {
      count: n,
      minimum: MIN_EVENTS,
    });
  }
  return computeWindow(n);
}

/** Inclusive gap, skipping year 0: -1 (1 BC) and 1 (AD 1) are 1 year apart. */
export function yearGap(a: number, b: number): number {
  if (a === 0 || b === 0) failYearZero();
  const raw = Math.abs(a - b);
  return (a < 0 && b > 0) || (a > 0 && b < 0) ? raw - 1 : raw;
}

export function formatYear(year: number): string {
  if (year === 0) failYearZero();
  return year < 0 ? `${Math.abs(year)} BC` : String(year);
}

export function parseUtcDate(isoDate: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) throw new Error(`launch date must be YYYY-MM-DD, got ${isoDate}`);
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Day 1 is the launch date. Each day starts at 00:00 UTC. */
export function utcDayNumber(now: Date, launchDate: string): number {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.floor((today - parseUtcDate(launchDate)) / 86_400_000) + 1;
}

const UTC_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** Calendar label for a schedule day number, e.g. `13 Nov 2026`. */
export function formatUtcDayLabel(dayNum: number, launchDate: string): string {
  const ms = parseUtcDate(launchDate) + (dayNum - 1) * 86_400_000;
  const d = new Date(ms);
  return `${d.getUTCDate()} ${UTC_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** After launch: today+1 through today+60. Before launch: days 1–61. */
export function validatorDayRange(now: Date, launchDate: string): { from: number; to: number } {
  const today = utcDayNumber(now, launchDate);
  if (today < 1) return { from: 1, to: PRE_LAUNCH_DAYS };
  return { from: today + 1, to: today + MAX_WINDOW };
}

export function frozenThroughDay(now: Date, launchDate: string): number {
  const today = utcDayNumber(now, launchDate);
  return today < 1 ? 0 : today;
}
